<template>
  <div class="flex items-end gap-2">
    <div
      v-if="supportChat"
      v-tooltip="'Добавить файл'"
    >
      <Button
        class="h-[58px] w-[58px] bg-white border-none focus:!outline-none focus:!shadow-none focus:!border-transparent"
        icon="pi pi-paperclip text-[#BDBDBD]"
        size="large"
        severity="secondary"
        :disabled="createMessagePending"
        @click="() => (visible = true)"
      />
    </div>

    <Textarea
      ref="messageInput"
      v-model="message"
      class="w-full min-h-[49px]"
      :class="
        supportChat
          ? 'border-none !h-[46px] pl-0 focus:!outline-none focus:!shadow-none focus:!border-transparent'
          : ''
      "
      autoResize
      placeholder="Сообщение.."
      :readonly="createMessagePending"
      @keydown="onKeydown"
      @paste="onPaste"
    />

    <div v-tooltip="createMessageDisabledReason">
      <Button
        v-if="supportChat"
        class="h-[58px] w-[58px] bg-white border-none focus:!outline-none focus:!shadow-none focus:!border-transparent"
        size="large"
        severity="secondary"
        :loading="createMessagePending"
        :disabled="createMessageDisabled"
        @mousedown.prevent
        @click="createMessage"
      >
        <template #icon>
          <div class="bg-[#F5F5F5] w-7 h-7 rounded-full">
            <img
              src="@/assets/support-chat/SendMessage.svg"
              class="flex-none shrink-0 max-h-10 w-auto object-contain"
              alt="chat-icon"
            />
          </div>
        </template>
      </Button>

      <Button
        v-if="!supportChat"
        class="h-[58px] w-[58px]"
        icon="pi pi-send"
        size="large"
        severity="secondary"
        :loading="createMessagePending"
        :disabled="createMessageDisabled"
        @mousedown.prevent
        @click="createMessage"
      />
    </div>

    <div
      v-if="!supportChat"
      v-tooltip="'Добавить файл'"
    >
      <Button
        class="h-[58px] w-[58px]"
        icon="pi pi-paperclip"
        size="large"
        severity="secondary"
        :disabled="createMessagePending"
        @click="() => (visible = true)"
      />
    </div>

    <Dialog
      v-model:visible="visible"
      class="min-w-[500px]"
      header="Создание сообщения с файлом"
      modal
      @after-hide="() => (file = null)"
    >
      <div class="py-4">
        <InputScan v-model="file" />

        <BaseDiver />

        <div class="flex items-end gap-2">
          <Textarea
            v-model="message"
            class="w-full min-h-[49px]"
            autoResize
            :readonly="createMessagePending"
            @keydown="onKeydown"
            @paste="onPaste"
          />

          <div v-tooltip="createMessageDisabledReason">
            <Button
              class="h-[58px] w-[58px]"
              icon="pi pi-send"
              size="large"
              severity="secondary"
              :loading="createMessagePending"
              :disabled="createMessageDisabled"
              @mousedown.prevent
              @click="createMessage"
            />
          </div>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import Button from 'primevue/button';
import Textarea from 'primevue/textarea';
import { useModel } from '../../model';
import { storeToRefs } from 'pinia';
import { Chatmessage } from 'simpleloan_api';
import Dialog from 'primevue/dialog';
import { computed, nextTick, ref, useTemplateRef } from 'vue';
import { BaseDiver, InputScan } from '@/shared';
import { useGlobalToast as useToast } from '@/shared/lib/toast';
import { useFocus } from '@vueuse/core';

const emits = defineEmits<{
  messageIsCreated: [Chatmessage];
}>();

defineProps<{
  supportChat?: boolean;
}>();

const visible = ref<boolean>(false);
const toast = useToast();

const model = useModel();
const { message, file, createMessageDisabled, createMessageDisabledReason, createMessagePending } =
  storeToRefs(model);

const messageInputRef = useTemplateRef<InstanceType<typeof Textarea>>('messageInput');

const textareaEl = computed(() => {
  const el = messageInputRef.value?.$el;

  return el instanceof HTMLTextAreaElement ? el : null;
});

const { focused: messageInputFocus } = useFocus(textareaEl, { initialValue: false });

/** настройки вставки */
const ALLOWED_MIME = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
  'image/svg+xml',
  'application/pdf'
]);

function onKeydown(event: KeyboardEvent) {
  // При нажатии на enter
  if (event.key === 'Enter') {
    // Если не зажата ни одна из этих клавиш
    if (!(event.shiftKey || event.ctrlKey)) {
      // Не даем сделать перенос на новую строчку
      event.preventDefault();
      createMessage();
    }

    // При зажатии ctrl
    if (event.ctrlKey) {
      // Почему-то не добавляем перенос поэтому добавляю руками
      message.value = message.value + '\n';
    }
  }
}

async function createMessage() {
  if (createMessagePending.value) return;

  const res = await model.createMessage();

  if (res?._code === 0 && res._data) {
    visible.value = false;
    emits('messageIsCreated', res._data);
  }

  await nextTick();
  messageInputFocus.value = true;

  return res;
}

/** обработчик Ctrl+V */
async function onPaste(e: ClipboardEvent) {
  const data = e.clipboardData;
  if (!data) return;

  const items = Array.from(data.items ?? []);
  const imageItems = items.filter(i => i.kind === 'file' && i.type.startsWith('image/'));

  // если нет картинок — позволяем стандартную вставку текста
  if (imageItems.length === 0) return;

  // предотвращаем попадание бинарных данных в textarea
  e.preventDefault();

  // отправляем каждое изображение отдельным сообщением
  for (const item of imageItems) {
    const pasted = item.getAsFile();
    if (!pasted) continue;

    // валидация
    if (!ALLOWED_MIME.has(pasted.type)) {
      toast.add({
        severity: 'warn',
        summary: 'Вставка изображения',
        detail: `Неразрешённый тип: ${pasted.type || 'unknown'}`
      });
      continue;
    }

    // модель читает реактивный file/message, просто проставляем и вызываем createMessage
    file.value = pasted;

    await createMessage();
    // чистим file/message на успешной отправке
    file.value = null;
  }
}
</script>
